import { toast } from "react-toastify/unstyled";
import { PROJECTS_ENDPOINT, SOFTWARE_CATALOG_ENDPOINT } from "Constants";
import { baseApi as api, listenToGroupUpdates } from "Services";
import {
  ApproveRequest,
  ApproveResponse,
  GroupUpdatesMessage,
  ImportExternalPackageResponse,
} from "Types";
import {
  CreatePackageRequest,
  CreatePackageResponse,
  CreateProjectRequest,
  CreateProjectResponse,
  DeletePackageRequest,
  DeleteProjectRequest,
  DeleteVersionRequest,
  DetachPackageRequest,
  GenerateOverviewRequest,
  GetProjectRequest,
  GetProjectResponse,
  ImportExternalPackageVersionsRequest,
  ImportVersionRequest,
  PackageOverview,
  ReprocessPackageRequest,
  UpdateProjectRequest,
} from "../types";
import { updateGetProjectCache } from "./websocketUpdates";

/** IMPORTANT: All cache updates are done through websockets,
 * there is no need to use provide/invalidate tags.
 *
 * In case you need to add a new API, make sure it has websocket support.
 */

export const projectsApi = api.injectEndpoints({
  endpoints: (build) => ({
    createProject: build.mutation<CreateProjectResponse, CreateProjectRequest>({
      query: (queryArg) => ({
        url: `${PROJECTS_ENDPOINT}/projects/create`,
        method: "POST",
        body: queryArg.body,
      }),
    }),
    getProject: build.query<GetProjectResponse, GetProjectRequest>({
      query: (queryArg) => ({
        url: `${PROJECTS_ENDPOINT}/projects/${queryArg.projectId}`,
      }),
      async onCacheEntryAdded(
        arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved, getState },
      ) {
        const notifications: Array<string | number> = [];

        const listener = (event: MessageEvent) => {
          const data = JSON.parse(event.data) as GroupUpdatesMessage;

          updateCachedData((draft) =>
            updateGetProjectCache(
              getState(),
              draft,
              data,
              arg.projectId,
              notifications,
            ),
          );
        };

        await listenToGroupUpdates(
          cacheDataLoaded,
          cacheEntryRemoved,
          listener,
          () => {
            notifications.forEach((notificationId) =>
              toast.dismiss(notificationId),
            );
          },
        );
      },
    }),
    updateProject: build.mutation<void, UpdateProjectRequest>({
      query: (queryArg) => ({
        url: `${PROJECTS_ENDPOINT}/projects/${queryArg.projectId}`,
        method: "PATCH",
        body: queryArg.body,
      }),
    }),
    deleteProject: build.mutation<void, DeleteProjectRequest>({
      query: (queryArg) => ({
        url: `${PROJECTS_ENDPOINT}/projects/${queryArg.projectId}`,
        method: "DELETE",
      }),
    }),
    createPackage: build.mutation<CreatePackageResponse, CreatePackageRequest>({
      query: (queryArg) => ({
        url: `${PROJECTS_ENDPOINT}/packages/create`,
        method: "POST",
        body: queryArg.body,
      }),
    }),
    deletePackage: build.mutation<void, DeletePackageRequest>({
      query: (queryArg) => ({
        url: `${PROJECTS_ENDPOINT}/packages/${queryArg.packageId}`,
        method: "DELETE",
      }),
    }),
    detachPackage: build.mutation<void, DetachPackageRequest>({
      query: (queryArg) => ({
        url: `${PROJECTS_ENDPOINT}/packages/${queryArg.packageId}/detach`,
        method: "DELETE",
      }),
    }),
    reprocessPackage: build.mutation<void, ReprocessPackageRequest>({
      query: (queryArg) => ({
        url: `${PROJECTS_ENDPOINT}/packages/${queryArg.packageId}/reprocess`,
        params: {
          notify_on_completion: queryArg.notify_on_completion,
        },
        method: "POST",
      }),
    }),
    generatePackageOverview: build.mutation<
      PackageOverview,
      GenerateOverviewRequest
    >({
      query: (queryArg) => ({
        url: `${PROJECTS_ENDPOINT}/packages/${queryArg.packageId}/generate-overview`,
        method: "POST",
      }),
    }),
    deleteVersion: build.mutation<void, DeleteVersionRequest>({
      query: (queryArg) => ({
        url: `${PROJECTS_ENDPOINT}/versions/${queryArg.versionId}`,
        method: "DELETE",
      }),
    }),
    approveVersion: build.mutation<ApproveResponse, ApproveRequest>({
      query(queryArg) {
        return {
          url: `${PROJECTS_ENDPOINT}/versions/${queryArg.subjectId}/approve`,
          method: "PUT",
          body: queryArg.body,
        };
      },
    }),
    importVersionFromUrl: build.mutation<void, ImportVersionRequest>({
      query: (queryArg) => ({
        url: `${PROJECTS_ENDPOINT}/versions/import`,
        params: {
          force: queryArg.force,
          replace: queryArg.replace,
          notify_on_completion: queryArg.notify_on_completion,
        },
        body: queryArg.body,
        method: "POST",
      }),
    }),
    deleteReproBuild: build.mutation<void, DeleteVersionRequest>({
      query: (queryArg) => ({
        url: `${PROJECTS_ENDPOINT}/versions/${queryArg.versionId}/repro`,
        method: "DELETE",
      }),
    }),
    importExternalPackageVersions: build.mutation<
      ImportExternalPackageResponse,
      ImportExternalPackageVersionsRequest
    >({
      query: ({ catalog, packageId, ...body }) => ({
        url: `${SOFTWARE_CATALOG_ENDPOINT}/external/${catalog}/packages/${packageId}/import`,
        method: "POST",
        body,
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useCreateProjectMutation,
  useGetProjectQuery,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  useCreatePackageMutation,
  useDeletePackageMutation,
  useDetachPackageMutation,
  useReprocessPackageMutation,
  useGeneratePackageOverviewMutation,
  useDeleteVersionMutation,
  useApproveVersionMutation,
  useImportVersionFromUrlMutation,
  useDeleteReproBuildMutation,
  useImportExternalPackageVersionsMutation,
} = projectsApi;
